import type { FC, MouseEvent } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import LaunchOutlined from '@mui/icons-material/LaunchOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import { useAnchorMenu } from 'hooks';

interface GoogleImageOpenMenuProps {
  sourceImageUrl?: string;
  storedImageUrl?: string;
  position?: 'top' | 'bottom';
  downloadProgress?: number | null;
  onOpenChange?: (open: boolean) => void;
  onDownloadImage: () => void;
}

const btnWhite = { color: 'white', fontSize: 10 } as const;
const iconSx = { mr: -0.5 } as const;

const GoogleImageOpenMenu: FC<GoogleImageOpenMenuProps> = ({
  sourceImageUrl,
  storedImageUrl,
  position = 'bottom',
  downloadProgress = null,
  onOpenChange,
  onDownloadImage,
}) => {
  const menu = useAnchorMenu();
  const isDownloading = downloadProgress !== null;

  const handleOpenMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    menu.handleOpenMenu(e);
    onOpenChange?.(true);
  };

  const handleCloseMenu = () => {
    menu.handleCloseMenu();
    onOpenChange?.(false);
  };

  const handleOpenImage = () => {
    if (!sourceImageUrl) return;
    window.open(sourceImageUrl, '_blank');
    handleCloseMenu();
  };

  return (
    <>
      <Button
        onClick={handleOpenMenu}
        variant="text"
        size="small"
        startIcon={<LaunchOutlined sx={iconSx} />}
        disabled={!sourceImageUrl && !storedImageUrl}
        sx={btnWhite}
      >
        Open
      </Button>
      <Menu
        anchorEl={menu.anchor}
        open={menu.isOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: position === 'bottom' ? 'top' : 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: position === 'bottom' ? 'bottom' : 'top', horizontal: 'center' }}
      >
        <MenuItem disabled={!sourceImageUrl} onClick={handleOpenImage}>
          <ListItemIcon>
            <ImageOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open</ListItemText>
        </MenuItem>
        <MenuItem disabled={!storedImageUrl || isDownloading} onClick={onDownloadImage}>
          <ListItemIcon>
            <DownloadOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{isDownloading ? 'Downloading...' : 'Download'}</ListItemText>
          {isDownloading && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {downloadProgress}%
            </Typography>
          )}
        </MenuItem>
      </Menu>
    </>
  );
};

export default GoogleImageOpenMenu;
